import type { UseFormReturn } from 'react-hook-form';

import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { LineUnit } from '~/api/common/types';
import type { Product } from '~/api/products/types';

import { useUser } from '~/api/user';
import { Title } from '~/components';
import { Combobox, Field, NumberInput } from '~/components/Form';
import { useProductCategories } from '~/hooks/useProductCategories';
import { insertIf } from '~/utils/arrays';
import { isWriter } from '~/utils/user';

import type { CreateEditProductFormType } from '../types';

import { gridStyle, sectionStyle } from './productSections.css';

type Props = {
  form: UseFormReturn<CreateEditProductFormType>;
  product: Product | null;
};

export const ProductDetailsSection = ({ form, product }: Props) => {
  const allProductCategories = useProductCategories();
  const { data: user } = useUser();
  const { t } = useTranslation(['lines', 'products', 'validation']);

  const { control, watch } = form;
  const watchProductCategories = watch('productCategories');

  // The typed text is offered as an option of its own so users can add categories that don't exist yet.
  const [categoryQuery, setCategoryQuery] = useState('');

  const unitOptions = useMemo(
    () =>
      (
        [
          'hours',
          'halfDays',
          'days',
          'kilometers',
          ...insertIf(isWriter(user) || product?.unit === 'words', 'words'),
          ...insertIf(isWriter(user) || product?.unit === 'characters', 'characters'),
        ] satisfies LineUnit[]
      ).map((unit) => ({ label: t(`lines:units.${unit}`, { count: 2 }), value: unit })),
    [product?.unit, t, user],
  );
  const unitItems = Combobox.useItems(unitOptions, { getLabel: (option) => option.label, getValue: (option) => option.value });

  const categoryOptions = useMemo(() => {
    const categories = [...new Set([...watchProductCategories, ...allProductCategories])].toSorted((a, b) => a.localeCompare(b));
    const query = categoryQuery.trim();
    const isNewCategory = !!query && !categories.some((category) => category.toLowerCase() === query.toLowerCase());

    return [...insertIf(isNewCategory, query), ...categories].map((category) => ({ label: category, value: category }));
  }, [allProductCategories, categoryQuery, watchProductCategories]);
  const categoryItems = Combobox.useItems(categoryOptions, { getLabel: (option) => option.label, getValue: (option) => option.value });

  return (
    <div className={sectionStyle}>
      <Title as="h2" styleAs="h3">
        {t('products:createEdit.details.title')}
      </Title>

      <div className={gridStyle}>
        {/* Price */}
        <Controller
          control={control}
          name="price"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label infotip={t('products:fields.price.tooltip')} optional>
                {t('products:fields.price.label')}
              </Field.Label>

              <NumberInput min={0} onBlur={onBlur} onValueChange={onChange} prefix="€" ref={ref} value={value} />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            max: { message: t('validation:max', { attribute: t('products:fields.price.label'), max: 100_000 }), value: 100_000 },
            min: { message: t('validation:min', { attribute: t('products:fields.price.label'), min: 0.01 }), value: 0.01 },
          }}
        />

        {/* Unit */}
        <Controller
          control={control}
          name="unit"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label infotip={t('products:fields.unit.tooltip')} optional>
                {t('products:fields.unit.label')}
              </Field.Label>

              <Combobox.Root items={unitItems} onValueChange={onChange} value={value}>
                <Combobox.Button placeholder={t('products:fields.unit.placeholder')} />

                <Combobox.List>
                  {(item: (typeof unitOptions)[number]) => (
                    <Combobox.Item key={item.value} value={item.value}>
                      {item.label}
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
        />

        {/* Categories */}
        <Controller
          control={control}
          name="productCategories"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label optional>{t('products:fields.categories.label')}</Field.Label>

              <Combobox.Root items={categoryItems} multiple onInputValueChange={setCategoryQuery} onValueChange={onChange} value={value}>
                <Combobox.ChipsButton getLabel={(category: string) => category} placeholder={t('products:fields.categories.placeholder')} />

                <Combobox.List>
                  {(item: (typeof categoryOptions)[number]) => (
                    <Combobox.Item key={item.value} value={item.value}>
                      {item.label}
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
        />
      </div>
    </div>
  );
};
